'use client';

import { RefObject, useEffect } from 'react';

type Options = {
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
};

/**
 * Fires `onImpression` once when the observed element first intersects the
 * browser viewport. Disconnects immediately after firing.
 *
 * No-op when IntersectionObserver is unavailable or `enabled` is false.
 */
export const useViewportImpressionOnce = (
  ref: RefObject<HTMLElement | null>,
  onImpression: () => void,
  { threshold = 0.25, rootMargin, enabled = true }: Options = {}
) => {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired) {
            fired = true;
            onImpression();
            observer.disconnect();
            break;
          }
        }
      },
      { root: null, threshold, rootMargin }
    );
    observer.observe(el);

    return () => observer.disconnect();
    // We intentionally re-run when `enabled` toggles on; `onImpression` is
    // expected to be stable from the caller (guarded with a ref).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
};
